import type { HoldCleaningImage } from "../models/hold-cleaning.models";
import { HoldCleaningSourceHint } from "./HoldCleaningSourceHint";
import { formatHoldCleaningDate, HoldCleaningAuthenticatedImage } from "./HoldCleaningUi";
import { IMAGE_CATEGORY_LABELS } from "./hold-cleaning-image-categories";
import { Dialog } from "@headlessui/react";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";

export const HoldCleaningImagePreviewModal = ({
  image,
  position,
  total,
  onClose,
  onPrevious,
  onNext,
}: {
  image: HoldCleaningImage | null;
  position: number;
  total: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}) => {
  useEffect(() => {
    if (!image || total < 2) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") onPrevious();
      if (event.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [image, total, onPrevious, onNext]);

  const holdLabel = image?.holdNumber == null ? "Unassigned hold" : `Hold ${image.holdNumber}`;

  return (
    <Dialog open={image !== null} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/75" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto px-3 py-4 sm:px-6">
        <div className="flex min-h-full items-center justify-center">
          <Dialog.Panel className="w-full max-w-6xl overflow-hidden rounded-xl bg-white text-[#292F2D] shadow-2xl dark:bg-gray-800 dark:text-gray-100">
            <header className="flex items-center justify-between gap-3 border-b border-[#DFE4E2] px-4 py-3 dark:border-gray-700 sm:px-6">
              <div>
                <Dialog.Title className="text-base font-semibold text-[#292F2D] dark:text-gray-100">
                  {holdLabel} image
                </Dialog.Title>
                <p className="text-xs text-[#4A5655] dark:text-gray-300">
                  Image {position} of {total}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close image preview"
                className="rounded-lg p-2 text-[#292F2D] hover:bg-[#F3F4F4] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475] dark:text-gray-100 dark:hover:bg-gray-900"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>
            {image && (
              <>
                <div className="flex min-h-64 items-center justify-center bg-[#17211F]">
                  <HoldCleaningAuthenticatedImage
                    key={image.imageId}
                    imageId={image.imageId}
                    alt={`${holdLabel} cleaning evidence, image ${position} of ${total}`}
                    preview
                  />
                </div>
                <footer className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
                  <div className="min-w-0 text-xs text-[#4A5655] dark:text-gray-300">
                    <p className="font-semibold">
                      {formatHoldCleaningDate(image.capturedAt, false)}
                      <HoldCleaningSourceHint source={image.source} />
                    </p>
                    {image.caption && <p className="mt-1 break-words">{image.caption}</p>}
                    {image.categoryMode === "SHADOW" && (
                      <p
                        className="mt-1"
                        title="Automatic visual category; not a cleanliness assessment or hold assignment."
                      >
                        Suggested category: {IMAGE_CATEGORY_LABELS[image.category ?? "UNCLASSIFIED"]}
                      </p>
                    )}
                  </div>
                  {total > 1 && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={onPrevious}
                        aria-label="Previous image"
                        className="rounded-lg border border-[#C7D0CE] p-2 text-[#176B62] hover:bg-[#F3F4F4] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475] dark:border-gray-600 dark:text-teal-200 dark:hover:bg-gray-900"
                      >
                        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={onNext}
                        aria-label="Next image"
                        className="rounded-lg border border-[#C7D0CE] p-2 text-[#176B62] hover:bg-[#F3F4F4] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475] dark:border-gray-600 dark:text-teal-200 dark:hover:bg-gray-900"
                      >
                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </footer>
              </>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};
